import React from "react";
import styled, { keyframes } from "styled-components";
import { useGlobalContext } from "./context";

const Modal = () => {
  const { isModalOpen, closeModal, correct, questions } = useGlobalContext();
  const totalQuestions = Math.max(questions.length, 1);
  const result = Math.round((correct / totalQuestions) * 100);
  const passed = result >= 60;
  return (
    <ModalContainer isOpen={isModalOpen}>
      <ModalContent>
        <TitleWrap>
          <HeaderIcon success={passed} aria-hidden="true">
            {passed ? "✨" : "⚡️"}
          </HeaderIcon>
          <Title success={passed}>
            {passed ? "Great Job!" : "Keep Going!"}
          </Title>
          <SubText>
            {passed
              ? "You’re acing this quiz—ready for another round?"
              : "Review the questions and give it another shot."}
          </SubText>
        </TitleWrap>

        <ScoreBadge success={passed}>
          <span>Score</span>
          <strong>{result}%</strong>
        </ScoreBadge>

        <ResultText>
          You answered <strong>{correct}</strong> out of{" "}
          <strong>{totalQuestions}</strong> questions correctly.
        </ResultText>
        <ButtonRow>
          <PlayAgainBtn type="button" onClick={closeModal}>
            Play again
          </PlayAgainBtn>
        </ButtonRow>
      </ModalContent>
    </ModalContainer>
  );
};

export default Modal;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const ModalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(12, 20, 39, 0.5);
  backdrop-filter: blur(6px);
  display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 200;
  transition: opacity 0.3s;
`;

const ModalContent = styled.div`
  background: #fff;
  border-radius: 1.5rem;
  box-shadow: 0 20px 80px rgba(30, 60, 114, 0.18);
  padding: 2.5rem 2rem;
  min-width: 320px;
  max-width: 520px;
  width: 90vw;
  text-align: center;
  animation: ${fadeIn} 0.35s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(
      circle at 20% 20%,
      rgba(30, 144, 255, 0.12),
      transparent 45%
    );
    pointer-events: none;
  }
`;

const Title = styled.h2`
  color: ${({ success }) => (success ? "#1e90ff" : "#ff4c60")};
  font-family: "Montserrat", sans-serif;
  font-size: 2rem;
  margin: 0.5rem 0 0.25rem 0;
`;

const ResultText = styled.p`
  color: #2a3340;
  font-size: 1.05rem;
  margin: 1.25rem 0 1.5rem 0;
`;

const PlayAgainBtn = styled.button`
  background: linear-gradient(120deg, #1e3c72 0%, #2a5298 50%, #5b8def 100%);
  color: #fff;
  border: none;
  border-radius: 999px;
  padding: 0.85rem 2.4rem;
  font-size: 1rem;
  font-family: "Montserrat", sans-serif;
  cursor: pointer;
  box-shadow: 0 12px 30px rgba(30, 60, 114, 0.25);
  transition: background 0.2s ease, transform 0.2s ease,
    box-shadow 0.2s ease;
  &:hover {
    background: linear-gradient(120deg, #2a5298 0%, #1e3c72 50%, #5b8def 100%);
    transform: translateY(-2px);
    box-shadow: 0 20px 40px rgba(30, 60, 114, 0.28);
  }
  &:focus-visible {
    outline: 2px solid #1e90ff;
    outline-offset: 3px;
  }
`;

const TitleWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  position: relative;
  z-index: 1;
`;

const HeaderIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: ${({ success }) =>
    success
      ? "linear-gradient(135deg, rgba(30,144,255,0.18), rgba(30,60,114,0.3))"
      : "linear-gradient(135deg, rgba(255,76,96,0.18), rgba(255,76,96,0.32))"};
  font-size: 1.5rem;
  color: ${({ success }) => (success ? "#1e3c72" : "#d23b52")};
`;

const SubText = styled.p`
  color: #4a5665;
  margin: 0;
  font-size: 0.95rem;
`;

const ScoreBadge = styled.div`
  margin: 1.5rem auto 0.75rem auto;
  width: min(320px, 100%);
  background: ${({ success }) =>
    success
      ? "linear-gradient(135deg, rgba(30,144,255,0.1), rgba(90,171,255,0.25))"
      : "linear-gradient(135deg, rgba(255,76,96,0.08), rgba(255,129,149,0.2))"};
  border: 1px solid ${({ success }) => (success ? "#9bc9ff" : "#ffc2cf")};
  color: #1e3c72;
  border-radius: 12px;
  padding: 0.9rem 1.1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 12px 30px rgba(30, 60, 114, 0.12);
  position: relative;
  z-index: 1;

  span {
    text-transform: uppercase;
    letter-spacing: 1px;
    font-size: 0.8rem;
    color: #4a5665;
  }

  strong {
    font-size: 1.4rem;
    color: #1e3c72;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 0.5rem;
  position: relative;
  z-index: 1;
`;
